import React from "react";
import { Link, useLocation } from "react-router-dom";
import { Helmet } from "react-helmet-async";

// Slug → human-readable label map. Unknown slugs fall back to a humanised version.
const LABEL_MAP = {
  "biography": "About",
  "paintings": "Paintings",
  "peoplescapes-new": "Peoplescapes — Recent",
  "peoplescapes-2023": "Peoplescapes 2018 - 2023",
  "peoplescapes-old": "Peoplescapes 2007 - 2017",
  "timemachine-2025": "Time Machine 2025",
  "sydneysiders-2021": "Sydneysiders: Unscripted",
  "passport-capsule-2026": "Passport Capsule Collection",
  "abstracts": "Abstracts",
  "cityscapes": "Cityscapes",
  "interiors": "Interiors",
  "misc": "Miscellaneous",
  "shop": "Shop",
  "passport-capsule": "Passport Capsule Collection",
  "prints": "Limited Edition Prints",
  "painting-shop": "Original Paintings",
  // Painting shop detail slugs
  "PigeonsDogsNewYorkers": "Pigeons, Dogs, and New Yorkers",
  "DriftingTogether2": "Drifting Together II",
  "SaintPaulMetro": "Saint-Paul Metro, Paris",
  "TheItalianRestaurant": "The Italian Restaurant",
  "BordersSkiersSpritzers": "Borders, Skiers, and Spritzers",
  "untitledInflatableRocket": "Untitled (Inflatable Rocket)",
  "LA5025Original": "Los Angeles 5025",
  "MillionYearsAgoOriginal": "150 Million Years Ago",
  "Optimist2Original": "The Optimist II",
  "ParisOriginal": "Paris: A Moveable Feast",
  "betoota": "Betoota Heights",
  "miniCapsuleOriginal": "10\" x 10\" Capsule Series",
  // Abstract detail slugs
  "twenty": "Twenty",
  "twentyFour": "Twenty Four",
  "twentyThree": "Twenty Three",
  "twentyFive": "Twenty Five",
  "twentySix": "Twenty Six",
  "twentySeven": "Twenty Seven",
  "twentyEight": "Twenty Eight",
  "twentyNine": "Twenty Nine",
  "thirteen": "Thirteen",
  "fifteen": "Fifteen",
  "sixteen": "Sixteen",
  "seventeen": "Seventeen",
  "nineteen": "Nineteen",
  "thirty": "Thirty",
  "thirtyOne": "Thirty One",
  "thirtyTwo": "Thirty Two",
  "thirtyFour": "Thirty Four",
  "thirtyFive": "Thirty Five",
  "thirtySix": "Thirty Six",
  "cadmiumMonster": "Cadmium Monster",
  "chromafluxin7": "Chromafluxin-7",
  "vibrantaseB14": "Vibrantase B-14",
  "polychromaX4": "Polychroma-X4",
  "pigmentoID9": "Pigmentol D-9",
  "spectrazyme35": "Spectrazyme 3.5",
  "untitled1": "Untitled",
  "untitled2": "Untitled (Small)",
  // Print detail slugs
  "eurosummer": "Euro Summer",
  "eastvillage": "A Day in East Village, NYC",
  "la5025": "Los Angeles The Year 5025",
  "thebusstop5": "The Bus Stop V",
  "hollywood": "Parenting in the Hollywood Hills",
};

// Some path segments are routing namespaces, not browseable destinations.
// We display them in the trail but render them as plain text instead of links.
const NON_LINKED_SEGMENTS = new Set([]);

function humanise(slug) {
  // Turn 'thirtyTwo' or 'thirty-two' into 'Thirty Two'
  return slug
    .replace(/[-_]+/g, " ")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function Breadcrumbs() {
  const { pathname } = useLocation();
  const segments = pathname.split("/").filter(Boolean);
  if (segments.length === 0) return null;

  // Build crumb list starting with Home
  const crumbs = [{ label: "Home", href: "/" }];
  let accumulated = "";
  segments.forEach((seg) => {
    accumulated += "/" + seg;
    const label = LABEL_MAP[seg] || humanise(seg);
    crumbs.push({ label, href: accumulated });
  });

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": crumbs.map((c, i) => ({
      "@type": "ListItem",
      "position": i + 1,
      "name": c.label,
      "item": `https://tyronelayne.com${c.href === "/" ? "" : c.href}`,
    })),
  };

  return (
    <>
      <Helmet>
        <script type='application/ld+json'>{JSON.stringify(jsonLd)}</script>
      </Helmet>
      <nav className='breadcrumbs' aria-label='Breadcrumb'>
        <ol className='breadcrumbsList'>
          {crumbs.map((c, i) => {
            const isLast = i === crumbs.length - 1;
            const isLinkable = !isLast && !NON_LINKED_SEGMENTS.has(c.href.split("/").pop());
            return (
              <li key={c.href} className='breadcrumbsItem'>
                {isLinkable ? (
                  <Link to={c.href} className='breadcrumbsLink'>
                    {c.label}
                  </Link>
                ) : (
                  <span
                    className={isLast ? "breadcrumbsCurrent" : "breadcrumbsLink"}
                    aria-current={isLast ? "page" : undefined}
                  >
                    {c.label}
                  </span>
                )}
                {!isLast && (
                  <span className='breadcrumbsSeparator' aria-hidden='true'>
                    ›
                  </span>
                )}
              </li>
            );
          })}
        </ol>
      </nav>
    </>
  );
}

export default Breadcrumbs;
